/**
 * Daily writing reminder (D4 of PRODUCT_PLAN).
 *
 * 100% local preference (localStorage, no push server):
 *   - enabled + HH:MM time.
 *   - Web/PWA: foreground timer → Notification API while the app is alive.
 *   - Tauri: notification scheduled in the OS (survives closing).
 *
 * If there is already an entry today, the daily notice is skipped (no nagging).
 */
import { createEffect, createSignal, onCleanup } from "solid-js";
import { msg } from "@lingui/core/macro";
import {
  DAILY_REMINDER_ID,
  DEFAULT_REMINDER_TIME,
  isValidReminderTime,
  localDateKey,
  msUntilReminder,
  nextReminderAt,
} from "@journal/shared";
import { getNotificationAdapter } from "@/lib/notifications/service";
import { useI18n } from "@/providers/i18n-provider";

const ENABLED_KEY = "journal.reminder.enabled";
const TIME_KEY = "journal.reminder.time";
/** Defensive timer re-check (system clock changes, suspension). */
const RESYNC_INTERVAL_MS = 60_000;

function readEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) === "1";
  } catch {
    return false;
  }
}

function readTime(): string {
  try {
    const raw = localStorage.getItem(TIME_KEY);
    return raw && isValidReminderTime(raw) ? raw : DEFAULT_REMINDER_TIME;
  } catch {
    return DEFAULT_REMINDER_TIME;
  }
}

const [enabled, setEnabledSignal] = createSignal(readEnabled());
const [time, setTimeSignal] = createSignal(readTime());

/** Shared preference state (module singleton). */
export function useReminderPreference() {
  const setEnabled = (value: boolean) => {
    setEnabledSignal(value);
    try {
      localStorage.setItem(ENABLED_KEY, value ? "1" : "0");
    } catch {
      /* no storage: the preference lives only in memory */
    }
  };

  const setTime = (value: string) => {
    if (!isValidReminderTime(value)) {
      return;
    }
    setTimeSignal(value);
    try {
      localStorage.setItem(TIME_KEY, value);
    } catch {
      /* no storage: the preference lives only in memory */
    }
  };

  return { enabled, time, setEnabled, setTime };
}

interface DailyReminderOptions {
  /** Key of the day with the most recent entry (to avoid notifying if you already wrote today). */
  lastEntryDay: () => string | undefined;
}

/**
 * Schedules the reminder while the app is mounted. Mount ONCE
 * (ReminderScheduler in AppProviders).
 */
export function useDailyReminder(options: DailyReminderOptions) {
  const { t } = useI18n();

  createEffect(() => {
    if (!enabled()) {
      void getNotificationAdapter().then(async (adapter) => adapter.cancelAll());
      return;
    }
    // oxlint-disable-next-line solid/reactivity -- intended snapshot: onCleanup clears the timer when time() changes, so each run uses its own value
    const reminderTime = time();

    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const fire = async () => {
      const adapter = await getNotificationAdapter();
      const wroteToday = options.lastEntryDay() === localDateKey();
      if (!wroteToday) {
        const granted = await adapter.requestPermission();
        if (granted) {
          await adapter.show(t(msg`Time to write`), {
            body: t(msg`A couple of lines is enough. How was your day?`),
            tag: DAILY_REMINDER_ID,
            data: { reminderId: DAILY_REMINDER_ID },
          });
        }
      }
      schedule();
    };

    const schedule = () => {
      if (cancelled) {
        return;
      }
      const ms = msUntilReminder(reminderTime);
      if (ms === null) {
        return;
      }
      clearTimeout(timer);
      // The timeout is rescheduled every minute as a defense against
      // suspended clocks; it only fires when it is really time.
      timer = setTimeout(() => {
        void fire();
      }, ms);
    };

    // OS-level (Tauri): in addition to the foreground timer, register the next
    // occurrence with the system so it can notify while the app is closed.
    const registerOsReminder = async () => {
      const adapter = await getNotificationAdapter();
      if (!adapter.supportsScheduling) {
        return;
      }
      const granted = await adapter.requestPermission();
      if (!granted) {
        return;
      }
      const next = nextReminderAt(reminderTime);
      if (next) {
        await adapter.schedule(
          { id: DAILY_REMINDER_ID, whenISO: next.toISOString() },
          {
            title: t(msg`Time to write`),
            body: t(msg`A couple of lines is enough. How was your day?`),
          },
        );
      }
    };
    void registerOsReminder();

    schedule();
    const resync = setInterval(schedule, RESYNC_INTERVAL_MS);

    onCleanup(() => {
      cancelled = true;
      clearTimeout(timer);
      clearInterval(resync);
    });
  });
}
